import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";
import { useTranslation } from '../../hooks/useTranslation';
import Wordmark from '../common/Wordmark';
import {
  HomeIcon,
  MessageCircleIcon,
  BookOpenIcon,
  LogOutIcon,
  LogInIcon,
  UserPlusIcon,
  PlusIcon,
  MenuIcon,
  CloseIcon,
  InstagramIcon,
  FacebookIcon,
  LinkedInIcon,
} from '../common/icons';

/**
 * Sidebar Component
 *
 * Main navigation sidebar with mobile-responsive hamburger menu.
 * - Desktop (≥768px): Fixed sidebar, always visible
 * - Mobile (<768px): Hidden by default, hamburger menu toggles overlay
 */
const Sidebar = ({ setIsPopupActive }) => {
  const { accounts, instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (inProgress === "none" && accounts.length > 0 && location.pathname === "/") {
      navigate("/");
    } else if (inProgress === "none" && accounts.length === 0 && location.pathname === "/") {
      navigate("/");
    }
  }, [accounts, inProgress, location.pathname, navigate]);

  const handleLogout = () => {
    instance.logoutPopup().then(() => {
      navigate("/");
    }).catch((error) => console.error('Logout error:', error));
  };

  const handleAuthPopup = (state) => {
    setIsPopupActive(state);
    setIsMobileMenuOpen(false);
  };

  // Check if current path matches link (or starts with for nested routes)
  const isActive = (path) => {
    if (path === '/home') return location.pathname === '/home' || location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Nav link styles - using new sidebar-active background
  const navLinkClasses = (path) => `
    mb-1 text-base font-medium rounded-lg px-3 py-3 flex items-center gap-3
    transition-all duration-200 ease-smooth min-h-[48px]
    ${isActive(path)
      ? 'bg-background-sidebar-active text-text-primary border-l-3 border-sage-500'
      : 'text-text-secondary hover:text-text-primary hover:bg-background-sidebar-active/50'
    }
  `;

  // Sidebar content - shared between desktop and mobile
  // Note: Mobile drawer has its own logo in the header, desktop shows logo here
  const SidebarContent = ({ showLogo = true }) => (
    <>
      <div>
        {/* Logo - only shown on desktop sidebar */}
        {showLogo && (
          <div className="mb-8 px-1 py-2">
            <Wordmark size="lg" />
          </div>
        )}

        {/* Navigation */}
        <nav className="flex flex-col" role="navigation" aria-label="Hovedmeny">
          {accounts.length > 0 ? (
            <>
              {/* Primary CTA */}
              <Link
                to="/new-conversation"
                className="mb-6 text-base text-white font-semibold bg-primary rounded-lg p-3 min-h-[48px] hover:bg-primary-600 transition-all duration-200 ease-smooth active:scale-[0.98] flex items-center justify-between"
              >
                <span>{t('sidebar.newConversation')}</span>
                <PlusIcon className="w-5 h-5" />
              </Link>

              {/* Nav Links */}
              <Link to="/home" className={navLinkClasses('/home')}>
                <HomeIcon className="w-5 h-5 flex-shrink-0" />
                <span>{t('sidebar.home')}</span>
              </Link>
              <Link to="/conversations" className={navLinkClasses('/conversations')}>
                <MessageCircleIcon className="w-5 h-5 flex-shrink-0" />
                <span>{t('sidebar.conversations')}</span>
              </Link>
              <Link to="/resources" className={navLinkClasses('/resources')}>
                <BookOpenIcon className="w-5 h-5 flex-shrink-0" />
                <span>{t('sidebar.resources')}</span>
              </Link>

              {/* Divider */}
              <div className="my-4 h-px bg-border-light" />

              <button
                onClick={handleLogout}
                className="mb-1 text-base font-medium text-text-muted hover:text-text-primary transition-all duration-200 hover:bg-background-sidebar-active/50 rounded-lg px-3 py-3 min-h-[48px] flex items-center gap-3 text-left"
              >
                <LogOutIcon className="w-5 h-5 flex-shrink-0" />
                <span>{t('sidebar.logout')}</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/" className={navLinkClasses('/')}>
                <HomeIcon className="w-5 h-5 flex-shrink-0" />
                <span>{t('sidebar.home')}</span>
              </Link>
              <button
                onClick={() => handleAuthPopup(true)}
                className="mb-1 text-base font-medium text-text-secondary hover:text-text-primary transition-all duration-200 hover:bg-background-sidebar-active/50 rounded-lg px-3 py-3 min-h-[48px] flex items-center gap-3 text-left"
              >
                <LogInIcon className="w-5 h-5 flex-shrink-0" />
                <span>{t('sidebar.signIn')}</span>
              </button>
              <button
                onClick={() => handleAuthPopup(true)}
                className="mt-2 text-base font-semibold text-white bg-primary rounded-lg text-center p-3 min-h-[48px] hover:bg-primary-600 transition-all duration-200 ease-smooth active:scale-[0.98] flex items-center justify-center gap-2"
              >
                <UserPlusIcon className="w-5 h-5" />
                <span>{t('sidebar.signUp')}</span>
              </button>
            </>
          )}
        </nav>
      </div>

      {/* Footer */}
      <div className="mt-auto border-t border-border-light pt-4">
        {accounts.length > 0 && (
          <Link
            to="/account/settings"
            className="text-sm text-text-secondary hover:text-text-primary transition-colors duration-200 block mb-2 py-1"
          >
            Konto
          </Link>
        )}
        <Link
          to="/about"
          className="text-sm text-text-muted hover:text-text-primary transition-colors duration-200 block mb-2 py-1"
        >
          {t('footer.about')}
        </Link>
        {accounts.length === 0 && (
          <Link
            to="/salgsvilkar"
            className="text-sm text-text-muted hover:text-text-primary transition-colors duration-200 block mb-2 py-1"
          >
            {t('footer.salesTerms')}
          </Link>
        )}
        <Link
          to="/privacy"
          className="text-sm text-text-muted hover:text-text-primary transition-colors duration-200 block mb-2 py-1"
        >
          {t('footer.privacy')}
        </Link>
        <Link
          to="/contact"
          className="text-sm text-text-muted hover:text-text-primary transition-colors duration-200 block mb-4 py-1"
        >
          {t('footer.contact')}
        </Link>

        {/* Social Links — same outline glyphs and URLs as the landing footer.
            p-3.5 around the w-5 glyph keeps the 48px tap target while the
            icon itself stays quiet; -ml-3.5 aligns the glyph edge with the
            footer links above. */}
        <div className="flex justify-start -ml-3.5">
          <a
            href="https://www.instagram.com/coord.no/"
            target="_blank"
            rel="noopener noreferrer"
            className="p-3.5 rounded-full text-text-muted hover:text-primary-600 transition-colors duration-200"
            aria-label="Coord på Instagram"
          >
            <InstagramIcon className="w-5 h-5" />
          </a>
          <a
            href="https://www.facebook.com/profile.php?id=61591598048896"
            target="_blank"
            rel="noopener noreferrer"
            className="p-3.5 rounded-full text-text-muted hover:text-primary-600 transition-colors duration-200"
            aria-label="Coord på Facebook"
          >
            <FacebookIcon className="w-5 h-5" />
          </a>
          <a
            href="https://www.linkedin.com/company/coord-as/"
            target="_blank"
            rel="noopener noreferrer"
            className="p-3.5 rounded-full text-text-muted hover:text-primary-600 transition-colors duration-200"
            aria-label="Coord på LinkedIn"
          >
            <LinkedInIcon className="w-5 h-5" />
          </a>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Header with Hamburger - icon on LEFT.
          safe-top sits on the OUTER div so the inset ADDS to the inner py-3
          — on one element .safe-top overrode py-3's padding-top entirely,
          collapsing it to 0 on devices without a notch (0px above the
          wordmark vs 12px below). Nesting keeps top/bottom symmetric without
          a calc()+env() value, which postcss-calc can't minify. */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-background-card border-b border-border-light safe-top">
        <div className="px-4 py-3 flex items-center">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 -ml-1 mr-3 text-text-primary hover:bg-background-sidebar-active rounded-lg transition-colors"
            aria-label={isMobileMenuOpen ? "Lukk meny" : "Åpne meny"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <CloseIcon className="w-[22px] h-[22px]" /> : <MenuIcon className="w-[22px] h-[22px]" />}
          </button>
          <Wordmark size="md" />
        </div>
      </div>

      {/* Mobile Menu Overlay - dimmed backdrop on the right strip */}
      <div
        className={`
          md:hidden fixed inset-0 z-40 bg-text-primary/50 backdrop-blur-sm
          transition-opacity duration-300
          ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
        onClick={() => setIsMobileMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Mobile Sidebar Drawer - 85-90% width */}
      <div
        className={`
          md:hidden fixed top-0 left-0 bottom-0 z-50
          w-[85vw] max-w-[320px]
          bg-background-card py-6 px-5 flex flex-col
          transform transition-transform duration-300 ease-out-soft
          shadow-xl
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        role="dialog"
        aria-modal="true"
        aria-label="Navigasjonsmeny"
      >
        {/* Drawer header with close button */}
        <div className="flex items-center justify-between mb-4">
          <Wordmark size="md" />
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-2 -mr-1 text-text-muted hover:text-text-primary hover:bg-background-sidebar-active rounded-lg transition-colors"
            aria-label="Lukk meny"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        <SidebarContent showLogo={false} />
      </div>

      {/* Desktop Sidebar - Hidden on mobile */}
      <div className="hidden md:flex bg-background-card h-full min-h-screen w-64 py-6 px-5 flex-col justify-between fixed z-20 shadow-md border-r border-border-light">
        <SidebarContent />
      </div>
    </>
  );
};

export default Sidebar;
